import {
  buildFormValuesFromFields,
  buildPayloadFromFields,
} from '@folio/components/forms/form-builder/form-fields-builders';
import { FormBuilderFooter } from '@folio/components/forms/form-builder/FormBuilderFooter';
import { useStepperContext } from '@folio/components/layout/StepperLayout/useStepperContext';
import { useQuery } from '@tanstack/react-query';
import {
  brokerageFirmControllerGetBrokerageFirmQueryOptions,
  keyValueFieldsToObject,
  useBrokerageFirmControllerCreateBrokerageFirm,
  useBrokerageFirmControllerUpdateBrokerageFirm,
  useConfigControllerGetPublicTenantConfig,
} from 'korint-sdk';
import type { FieldShortcut } from 'korint-sdk/react';
import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { SectionedFormBuilder, getResolver, resolveFieldShortcuts } from '../../components';
import { isFieldVisible } from '../../components/forms/form-builder/conditionalVisibilityUtils';

interface IntermediaryFunnelModuleProps {
  /**
   * The fields to display in the single section of this step. A bare key resolves its type and
   * validation from the tenant's `brokerageFirmCustomFields`.
   */
  fields: Array<FieldShortcut>;
  /**
   * The path of this step, used as the discriminant for the section title:
   * `intermediaryFunnel.sections.{path}.title`.
   */
  path: string;
}

export function IntermediaryFunnelModule({
  fields: fieldShortcuts,
  path,
}: IntermediaryFunnelModuleProps) {
  const { t } = useTranslation();
  const { brokerageFirmId, parentBrokerageFirmId } = useParams();
  const stepper = useStepperContext();

  const { data: tenantConfig, isLoading: isTenantConfigLoading } =
    useConfigControllerGetPublicTenantConfig();

  const fields = useMemo(
    () => resolveFieldShortcuts(fieldShortcuts, tenantConfig?.brokerageFirmCustomFields ?? []),
    [fieldShortcuts, tenantConfig],
  );

  const { data: brokerageFirm, isLoading: isBrokerageFirmLoading } = useQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(brokerageFirmId!),
    enabled: !!brokerageFirmId,
    select: data => ({
      ...data,
      customFields: keyValueFieldsToObject(data.customFields ?? []),
    }),
  });

  const { mutateAsync: createBrokerageFirm } = useBrokerageFirmControllerCreateBrokerageFirm();
  const { mutateAsync: updateBrokerageFirm } = useBrokerageFirmControllerUpdateBrokerageFirm();

  const form = useForm({
    values: buildFormValuesFromFields(fields, brokerageFirm),
    resolver: getResolver(fields),
  });

  const persist = useRef<() => void>(() => undefined);
  persist.current = () => {
    const { dirtyFields } = form.formState;
    if (brokerageFirmId && Object.keys(dirtyFields).length > 0) {
      const values = form.getValues();
      const visibleFields = fields.filter(field => isFieldVisible(field, values));
      const payload = buildPayloadFromFields(visibleFields, values, dirtyFields);
      updateBrokerageFirm({ brokerageFirmId, data: payload }).catch(() => undefined);
    }
  };

  // Save on unmount only: empty deps fire the cleanup once, and the ref keeps it
  // reading the latest dirty fields rather than a stale first-render closure.
  useEffect(() => () => persist.current(), []);

  const onSubmit = form.handleSubmit(async data => {
    const visibleFields = fields.filter(field => isFieldVisible(field, data));
    const payload = buildPayloadFromFields(visibleFields, data);

    if (brokerageFirmId) {
      await updateBrokerageFirm({
        brokerageFirmId,
        data: payload,
      });
      form.reset(data);
      stepper.next();
    } else {
      const newBrokerageFirm = await createBrokerageFirm({
        data: {
          ...payload,
          firmType: parentBrokerageFirmId ? 'RETAIL' : 'WHOLESALE',
          parentBrokerageFirmId,
        },
      });
      stepper.next({ brokerageFirmId: newBrokerageFirm.id });
    }
  });

  return (
    <SectionedFormBuilder
      sections={[{ title: t(`intermediaryFunnel.sections.${path}.title`), fields }]}
      form={form}
      isLoading={isBrokerageFirmLoading || isTenantConfigLoading}
      externalData={brokerageFirm}
    >
      <FormBuilderFooter
        mode="stepper"
        onSubmit={onSubmit}
        form={form}
        showPreviousButton={!stepper.isFirstStep}
      />
    </SectionedFormBuilder>
  );
}
